import { Controller } from "@hotwired/stimulus"

// Wraps navigator.credentials.create()/get() for passkey registration
// (Customers::Dashboard::WebauthnCredentialsController) and passkey login
// (Customers::WebauthnSessionsController). Both ceremonies share the same
// two-step shape: fetch options from the server, hand them to the browser's
// WebAuthn API, then POST the browser's response back for verification.
//
//   <button data-controller="webauthn"
//           data-webauthn-options-url-value="..."
//           data-webauthn-verify-url-value="..."
//           data-action="click->webauthn#register|authenticate">
//   <p data-webauthn-target="error" class="hidden"></p>
export default class extends Controller {
  static targets = ["error", "button"]
  static values = { optionsUrl: String, verifyUrl: String, navigateOnSuccess: { type: Boolean, default: true } }

  connect() {
    // No conforming client — hide the trigger rather than let it fail.
    if (!window.PublicKeyCredential) this.element.hidden = true
  }

  async register(event) {
    event.preventDefault()
    this._clearError()

    try {
      const options = await this._postJSON(this.optionsUrlValue, {})
      const publicKey = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        user: { ...options.user, id: base64urlToBuffer(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: base64urlToBuffer(c.id) }))
      }

      const credential = await navigator.credentials.create({ publicKey })
      const result = await this._postJSON(this.verifyUrlValue, {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            attestationObject: bufferToBase64url(credential.response.attestationObject)
          }
        }
      })

      if (this.navigateOnSuccessValue) {
        if (result.redirect_url) window.location.href = result.redirect_url
      } else {
        // Caller wants to stay put (e.g. the dashboard "enable fingerprint
        // login?" prompt) — let it react instead of navigating away.
        this.dispatch("registered", { detail: result })
      }
    } catch (error) {
      this._showError(error)
    }
  }

  async authenticate(event) {
    event.preventDefault()
    this._clearError()

    try {
      const options = await this._postJSON(this.optionsUrlValue, {})
      const publicKey = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: base64urlToBuffer(c.id) }))
      }

      const credential = await navigator.credentials.get({ publicKey })
      const result = await this._postJSON(this.verifyUrlValue, {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            authenticatorData: bufferToBase64url(credential.response.authenticatorData),
            signature: bufferToBase64url(credential.response.signature),
            userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
          }
        }
      })

      if (result.redirect_url) window.location.href = result.redirect_url
    } catch (error) {
      this._showError(error)
    }
  }

  // Private

  async _postJSON(url, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || response.statusText)
    return data
  }

  _showError(error) {
    // The user simply dismissed the browser's passkey prompt — not a real error.
    if (error.name === "NotAllowedError") return

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = error.message
      this.errorTarget.classList.remove("hidden")
    }
  }

  _clearError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}
